import clsx from 'clsx';
import { TableVirtuoso } from 'react-virtuoso';
import { HeaderRow } from './HeaderRow';
import { Checkbox, makeStyles, Radio } from '@material-ui/core';
import { SyntheticEvent, useEffect, useState } from 'react';
import { TableActions } from './TableActions';
import { graphingColors } from '../../lib/constants';
import { DynamicSkeleton } from '../DynamicSkeleton';
import { NoDataMessage } from '../NoDataMessage';
import { useOperator } from '../../contexts/OperatorContext';

const buildLegend = (
  data: any[] | undefined,
  externalFilter: FilterOption | FilterOption[] | undefined,
  dataKey: string,
  legendColors?: string[] | undefined
): StatisticsData[] => {
  if (data === undefined || Array.isArray(externalFilter)) return [];

  let uniqueValues: string[] = [];
  const defaultColors = graphingColors[0].colors;
  const colors = legendColors !== undefined ? legendColors : defaultColors;

  if (typeof data === 'object' && data.length) {
    const keys = Object.keys(data[0]) ?? [];

    if (!keys.includes(dataKey)) return [];

    data.forEach((row) => {
      const value = row[dataKey];
      if (!uniqueValues.includes(value)) uniqueValues.push(value);
    });
  }

  return uniqueValues
    .sort((a, b) => (a < b ? -1 : 1))
    .map((value, index) => ({
      id: value,
      count: data.filter(
        (d) =>
          d[dataKey] === value &&
          (externalFilter?.filterTerm
            ? (d[externalFilter?.column ?? ''] || '').toLowerCase() ===
              (externalFilter?.filterTerm.toString() || '').toLowerCase()
            : true)
      ).length,
      color: colors[index],
      value: value,
    }));
};

export const Table = (props: TableProps) => {
  const {
    id,
    clickable = false,
    columns,
    csvName,
    customTool,
    data,
    dataRenderProps,
    disableToolbar = false,
    disabled = false,
    dynamicHeight = false,
    expandedContent,
    expandSearch = false,
    externalFilter,
    formatters,
    height = 500,
    legendColors,
    legendFromColumn,
    loading = false,
    onClearFilter,
    multiSelect = false,
    noDataMessage,
    onClick,
    onSelectAll,
    rowHeight = 50,
    selectedItems,
    showTotalRows = false,
    singleSelect,
    tools,
  } = props;
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortAsc, setSortAsc] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [filteredData, setFilteredData] = useState(data);
  const [displayData, setDisplayData] = useState(filteredData);
  const [filteredColumns, setFilteredColumns] = useState(columns);

  const operator = useOperator();
  const legendData = buildLegend(
    data,
    externalFilter,
    legendFromColumn ?? '',
    legendColors
  );
  let realHeight;

  if (displayData.length && height > (displayData.length + 1) * rowHeight)
    realHeight = (displayData.length + 1) * rowHeight;

  const calculatedHeight = !disableToolbar
    ? realHeight ?? height
    : `calc(${height})`;

  const classes = useStyles(
    rowHeight,
    dynamicHeight ? calculatedHeight : undefined
  )();

  const tableHeight =
    height !== undefined
      ? realHeight ?? height
      : disableToolbar
      ? '100%'
      : 'calc(100% - 50px)';

  useEffect(() => {
    setFilteredColumns(
      columns
        .filter((c) => c.show ?? true)
        .sort((a, b) => {
          if (a.order !== undefined && b.order !== undefined)
            return a.order < b.order ? -1 : 1;
          else if (a.order !== undefined && b.order === undefined) return -1;
          else if (b.order !== undefined && a.order === undefined) return 1;
          else return 0;
        })
    );
    setFilteredData(data);
  }, [data, columns]);

  useEffect(() => {
    setDisplayData([]);
  }, [operator.isChanging]);

  useEffect(() => {
    const searchData = filteredData.filter((data) =>
      Object.values(data)
        .join('|')
        .toLowerCase()
        .includes(searchValue.toLowerCase())
    );

    setDisplayData(() => {
      if (externalFilter) {
        return searchData.filter((sd: any) => {
          if (Array.isArray(externalFilter)) {
            if (matchesExternalFilterArray(sd, externalFilter)) return sd;
          } else if (matchesExternalFilter(sd, externalFilter)) return sd;
        });
      }
      return searchData;
    });
  }, [filteredData, searchValue, externalFilter, multiSelect, singleSelect]);

  const matchesExternalFilterArray = (
    searchDataItem: any,
    filters: FilterOption[]
  ) => {
    let colValue = null;
    let matches = 0;

    filters.forEach((filter: FilterOption) => {
      colValue = filter.comparer
        ? filter.comparer(searchDataItem[filter.column])
        : searchDataItem[filter.column];

      if (colValue === filter.filterTerm) matches++;
    });
    return (
      matches ===
      filters.reduce((results: FilterOption[], filter) => {
        if (results.findIndex((f) => f.column === filter.column) === -1)
          results.push(filter);
        return results;
      }, []).length
    );
  };

  const matchesExternalFilter = (searchDataItem: any, filter: FilterOption) => {
    const colValue = filter.comparer
      ? filter.comparer(searchDataItem[filter.column])
      : searchDataItem[filter.column];
    return colValue !== undefined && colValue === filter.filterTerm;
  };

  const handleSelectAll = (selected: boolean): void => {
    if (onSelectAll === undefined) return;
    const items = displayData.reduce((arr: any[], data) => {
      const matchedArrIndex = arr.findIndex(
        (d: any) => Object.values(d).join('') === Object.values(data).join('')
      );
      if (selected && matchedArrIndex === -1) arr.push(data);
      else if (!selected && matchedArrIndex > -1)
        arr.splice(matchedArrIndex, 1);

      return arr;
    }, new Array(...(selectedItems ?? [])));

    onSelectAll(items);
  };

  const handleClick = (event: SyntheticEvent, row: any) => {
    if (onClick !== undefined) {
      event.stopPropagation();
      event.preventDefault();

      if (multiSelect) {
        const itemIndex = (selectedItems ?? []).findIndex(
          (i) => Object.values(i).join('') === Object.values(row).join('')
        );
        let items: any[];

        if (itemIndex === -1) {
          if (multiSelect) items = [...(selectedItems ?? []), row];
          else items = [row];
        } else {
          if (multiSelect)
            items = (selectedItems ?? []).filter(
              (d, index) => index !== itemIndex
            );
          else items = [];
        }

        onClick(row, items);
      } else {
        onClick(row, [row]);
      }
    }
  };

  const handleSort = (dataKey: string) => {
    setSortColumn(dataKey);

    if (sortColumn === dataKey) {
      displayData.reverse();
      setSortAsc((sortAsc) => !sortAsc);
    } else {
      displayData.sort((a, b) => {
        let result = 0;
        if (a[dataKey] > b[dataKey]) result = 1;
        if (a[dataKey] < b[dataKey]) result = -1;
        return sortAsc ? result : result * -1;
      });
    }
  };

  const renderTableCell = (row: any, key: string): any => {
    const render = dataRenderProps?.find((d) => d.id === key);
    const value = row[key]?.value ?? row[key];
    const color =
      legendData.length > 0
        ? legendData.find((d) => d.id === value)?.color
        : undefined;

    if (dataRenderProps !== undefined && render !== undefined)
      return render.component(value, color, row);

    return value;
  };

  const loadingState = (
    <tr className="h-full">
      <td colSpan={columns.length}>
        <DynamicSkeleton
          direction="column"
          rowHeight={rowHeight}
          skeletons={['100%', '100%', '100%', '100%', '100%']}
        />
      </td>
    </tr>
  );

  return (
    <div className={classes.container}>
      <TableActions
        customTool={customTool}
        csvName={csvName}
        data={data}
        displayData={displayData}
        disableToolbar={disableToolbar}
        expandSearch={expandSearch}
        externalFilter={externalFilter}
        filteredColumns={filteredColumns}
        filteredData={filteredData}
        formatters={formatters}
        legendData={legendData}
        legendFromColumn={legendFromColumn ?? ''}
        loading={loading}
        onClearFilter={onClearFilter}
        searchValue={searchValue}
        setFilteredColumns={setFilteredColumns}
        setFilteredData={setFilteredData}
        setSearchValue={setSearchValue}
        tools={tools}
      />
      <TableVirtuoso
        id={id}
        className="no-overflow-anchoring"
        increaseViewportBy={{ bottom: 1, top: 1 }}
        style={{
          height: height,
          minHeight: tableHeight,
        }}
        components={{
          Table: ({ children }) => (
            <table className={classes.table}>{children}</table>
          ),
          EmptyPlaceholder: () =>
            loading ? (
              <tbody>{loadingState}</tbody>
            ) : (
              <tbody>
                <tr>
                  <td colSpan={columns.length}>
                    <div className={classes.noDataMessage}>
                      <NoDataMessage
                        title={noDataMessage?.title}
                        subTitle={noDataMessage?.subTitle}
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            ),
          TableRow: (props) => {
            const index = props['data-index'];
            const row = displayData[index];
            return loading ? (
              loadingState
            ) : (
              <>
                <tr
                  className={clsx(
                    classes.row,
                    {
                      '!bg-primary/20': selectedItems?.includes(row),
                    },
                    {
                      [classes.clickableRow]:
                        clickable || multiSelect || singleSelect,
                    },
                    {
                      'pointer-events-none opacity-30': disabled,
                    }
                  )}
                  onClick={(event) => handleClick(event, row)}
                  {...props}
                />
                {expandedContent !== undefined &&
                selectedItems?.includes(row) ? (
                  <tr>
                    <td colSpan={columns.length}>{expandedContent}</td>
                  </tr>
                ) : null}
              </>
            );
          },
        }}
        data={displayData}
        fixedHeaderContent={() => (
          <HeaderRow
            classes={classes}
            columns={filteredColumns}
            displayDataCount={displayData.length}
            loading={loading}
            multiSelect={multiSelect}
            onSelectAll={handleSelectAll}
            onSort={handleSort}
            selectAllItems={
              multiSelect
                ? displayData.every((i) =>
                    (selectedItems ?? []).some(
                      (s: any) =>
                        Object.values(s).join('') === Object.values(i).join('')
                    )
                  )
                : undefined
            }
            singleSelect={singleSelect}
            sortAsc={sortAsc}
            sortColumn={sortColumn}
          />
        )}
        itemContent={(index, item) => {
          return (
            <>
              {multiSelect ? (
                <td className={classes.center}>
                  <Checkbox
                    className="z-0"
                    checked={selectedItems?.includes(item)}
                    color="primary"
                    disabled={disabled}
                    size="small"
                  />
                </td>
              ) : singleSelect ? (
                <td className={classes.center}>
                  <Radio
                    checked={selectedItems?.includes(item)}
                    color="primary"
                    disabled={disabled}
                    size="small"
                  />
                </td>
              ) : null}
              {filteredColumns
                .filter((c) => !c.isHidden)
                .map((c, i) => (
                  <td
                    className={clsx(classes.center, classes.cell, {
                      'sticky bg-card-dark text-white': c.sticky !== undefined,
                      'left-0': c.sticky && c.sticky === 'left',
                      'right-0': c.sticky && c.sticky === 'right',
                    })}
                    style={{ width: c.width, minWidth: c.minWidth }}
                    key={c.name + i}
                  >
                    {renderTableCell(item, c.dataKey)}
                  </td>
                ))}
            </>
          );
        }}
      />
      {showTotalRows ? (
        <div className="w-full p-2 border-t border-border-light dark:border-border-dark">
          <p>
            Rows:{' '}
            {searchValue !== '' ||
            (externalFilter !== undefined &&
              Array.isArray(externalFilter) &&
              externalFilter.length) ? (
              <>
                <span className="text-primary">
                  {displayData.length.toLocaleString('en-US')}
                </span>
                /
              </>
            ) : (
              ''
            )}
            {data.length.toLocaleString('en-US')}
            {selectedItems !== undefined && selectedItems.length ? (
              <span className="rounded-full ml-2 px-2 text-white bg-primary">
                {selectedItems.length}
              </span>
            ) : (
              ''
            )}
          </p>
        </div>
      ) : null}
    </div>
  );
};

const useStyles = (rowHeight: number, height?: number | string) =>
  makeStyles((theme: any) => {
    return {
      cell: {
        whiteSpace: 'nowrap',
        height: rowHeight,
        paddingInline: theme.spacing(1),
      },
      center: {
        textAlign: 'center',
      },
      clickableRow: {
        cursor: 'pointer',
      },
      container: {
        height: height,
        width: '100%',
      },
      header: {
        backgroundColor: theme.palette.table.header,
        color: theme.palette.table.headerText,
      },
      loading: {
        position: 'absolute',
        width: '100%',
      },
      noDataMessage: {
        height: 300,
      },
      rowBackground: {
        backgroundColor: theme.palette.paper.dark,
      },
      row: {
        height: rowHeight,
        borderBottom: `1px solid ${theme.palette.table.border}`,
      },
      table: {
        width: '100%',
        borderCollapse: 'collapse',
        '& tr:nth-child(even)': {
          backgroundColor: theme.palette.table.row,
        },
      },
      selectedRow: {
        color: `${theme.palette.primary.main} !important`,
      },
    };
  });
